import React, { useState, useEffect } from 'react';
import { Clock, Users, TrendingUp, AlertCircle, Play, Pause, CheckCircle, Eye, Filter, RefreshCw, X, UserPlus, Trash2, Edit } from 'lucide-react';
import api from '../../services/api';
import EnquiryDetailsModal from './EnquiryDetailsModal';

interface TeamMember {
  id: number;
  name: string;
  email: string;
  active_assignments: number;
  completed_today: number;
  hours_worked_today: number;
  avg_completion_time: number;
  efficiency_score: number;
  total_itineraries_created: number;
  itineraries_created_today: number;
  enquiry_requests_completed: number;
  avg_time_per_request: number;
  avg_time_per_itinerary: number;
  total_pause_time: number;
}

interface Assignment {
  id: number;
  enquiry_id: number;
  agency_name: string;
  customer_name?: string;
  travel_date: string;
  plan_text: string;
  priority: string;
  status: string;
  pause_reason?: string;
  assigned_to: number;
  assigned_to_name: string;
  assigned_at: string;
  started_at?: string;
  completed_at?: string;
  actual_hours?: number;
  cumulative_work_minutes?: number;
  number_of_adults?: number;
  number_of_kids?: number;
}

interface DashboardStats {
  total_enquiries_today: number;
  total_assignments_today: number;
  completed_assignments_today: number;
  avg_completion_time: number;
  total_hours_worked_today: number;
  overdue_assignments: number;
  urgent_pending: number;
  team_utilization: number;
}

const WorkDashboard: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [selectedEnquiry, setSelectedEnquiry] = useState<Assignment | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [reassignUserId, setReassignUserId] = useState<number | null>(null);
  const [teamDateFrom, setTeamDateFrom] = useState('');
  const [teamDateTo, setTeamDateTo] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const _today = new Date().toISOString().split('T')[0];
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    assigned_to: '',
    created_from: _today,
    created_to: _today,
    enquiryId: '',
    requestId: '',
    agencyName: '',
    customerName: ''
  });
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async (fromDate = '', toDate = '') => {
    try {
      setLoading(true);
      let teamUrl = '/team-members';
      if (fromDate || toDate) {
        const params = new URLSearchParams();
        if (fromDate) params.append('from_date', fromDate);
        if (toDate) params.append('to_date', toDate);
        teamUrl += `?${params.toString()}`;
      }
      const [teamResponse, assignmentsResponse, statsResponse] = await Promise.all([
        api.get(teamUrl),
        api.get('/work-assignments'),
        api.get('/work-assignments/dashboard-stats')
      ]);

      if (teamResponse.data.success) setTeamMembers(teamResponse.data.data);
      if (assignmentsResponse.data.success) setAssignments(assignmentsResponse.data.data);
      if (statsResponse.data.success) setStats(statsResponse.data.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTeamMembers = teamMembers.filter(member => 
    teamFilter === '' || member.name.toLowerCase().includes(teamFilter.toLowerCase()) || member.email.toLowerCase().includes(teamFilter.toLowerCase())
  );

  const formatHours = (hours: number) => {
    if (hours <= 0) return '-';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const filteredAssignments = assignments.filter(assignment => {
    if (filters.status && assignment.status !== filters.status) return false;
    if (filters.priority && assignment.priority !== filters.priority) return false;
    if (filters.assigned_to && assignment.assigned_to.toString() !== filters.assigned_to) return false;
    if (filters.created_from) {
      const assignedDate = new Date(assignment.assigned_at);
      const fromDate = new Date(filters.created_from + 'T00:00:00');
      if (assignedDate < fromDate) return false;
    }
    if (filters.created_to) {
      const assignedDate = new Date(assignment.assigned_at);
      const toDate = new Date(filters.created_to + 'T23:59:59.999');
      if (assignedDate > toDate) return false;
    }
    if (filters.enquiryId && !assignment.enquiry_id.toString().includes(filters.enquiryId)) return false;
    if (filters.agencyName && !assignment.agency_name.toLowerCase().includes(filters.agencyName.toLowerCase())) return false;
    if (filters.customerName && !assignment.customer_name?.toLowerCase().includes(filters.customerName.toLowerCase())) return false;
    return true;
  });

  const filteredStats = React.useMemo(() => {
    const today = new Date().toDateString();
    return {
      total_enquiries_today: filteredAssignments.filter(a => new Date(a.assigned_at).toDateString() === today).length,
      completed_assignments_today: filteredAssignments.filter(a => a.status === 'completed' && a.completed_at && new Date(a.completed_at).toDateString() === today).length,
      total_hours_worked_today: filteredAssignments.filter(a => a.completed_at && new Date(a.completed_at).toDateString() === today).reduce((sum, a) => sum + (a.actual_hours || 0), 0),
      team_utilization: filteredAssignments.filter(a => a.status === 'in_progress').length / Math.max(1, filteredAssignments.length) * 100
    };
  }, [filteredAssignments]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'assigned': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const calculateWorkingTime = (assignment: Assignment) => {
    // Use actual_hours if available (for completed assignments)
    if (assignment.actual_hours) {
      const hours = Math.floor(assignment.actual_hours);
      const minutes = Math.round((assignment.actual_hours - hours) * 60);
      return `${hours}h ${minutes}m`;
    }
    
    // Use cumulative_work_minutes for active/paused assignments
    if (assignment.cumulative_work_minutes) {
      const hours = Math.floor(assignment.cumulative_work_minutes / 60);
      const minutes = assignment.cumulative_work_minutes % 60;
      return `${hours}h ${minutes}m`;
    }
    
    return '0h 0m';
  };

  const getActiveWorkTime = (assignment: Assignment) => {
    // For completed assignments, use actual_hours
    if (assignment.actual_hours) {
      return assignment.actual_hours * 60; // Convert to minutes
    }
    
    // For active/paused assignments, use cumulative_work_minutes
    return assignment.cumulative_work_minutes || 0;
  };

  const isAssignmentOverdue = (assignment: Assignment) => {
    if (assignment.status !== 'in_progress') return false;
    const activeMinutes = getActiveWorkTime(assignment);
    return activeMinutes > 30;
  };

  const handleViewEnquiry = async (assignment: Assignment) => {
    try {
      const response = await api.get(`/enquiry-details/${assignment.enquiry_id}`);
      if (response.data.success) {
        setSelectedEnquiry({ ...assignment, ...response.data.data });
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching enquiry details:', error);
    }
  };

  const handleReassign = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setReassignUserId(null);
    setShowReassignModal(true);
  };

  const confirmReassign = async () => {
    if (!selectedAssignment || !reassignUserId) return;
    try {
      const response = await api.post(`/work-assignments/reassign/${selectedAssignment.enquiry_id}`, {
        assigned_to: reassignUserId
      });
      if (response.data.success) {
        alert(response.data.message);
        setShowReassignModal(false);
        fetchDashboardData();
      }
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to reassign');
    }
  };

  const handleDelete = async (_enquiryId: number) => {
    alert('Please use the Enquiry Details modal to delete individual requests.');
  };

  const handleEdit = (assignment: Assignment) => {
    window.location.href = `/create-enquiry?edit=${assignment.enquiry_id}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Work Dashboard</h1>
            <p className="text-gray-600 mt-1">Team productivity and assignment overview</p>
          </div>
          <button
            onClick={() => fetchDashboardData()}
            className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        {filteredStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Today's Enquiries</p>
                  <p className="text-2xl font-bold text-blue-600">{filteredStats.total_enquiries_today}</p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Completed Today</p>
                  <p className="text-2xl font-bold text-green-600">{filteredStats.completed_assignments_today}</p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Hours Worked</p>
                  <p className="text-2xl font-bold text-purple-600">{filteredStats.total_hours_worked_today.toFixed(1)}h</p>
                </div>
                <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Team Utilization</p>
                  <p className="text-2xl font-bold text-orange-600">{filteredStats.team_utilization.toFixed(0)}%</p>
                </div>
                <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <Users className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Team Performance & Filters */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >
              <Filter className="h-4 w-4 mr-2" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
          </div>
          
          {showFilters && (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 mb-6">
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Enquiry ID</label>
                  <input
                    type="text"
                    placeholder="Search by ID..."
                    value={filters.enquiryId}
                    onChange={(e) => setFilters(prev => ({ ...prev, enquiryId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Agency Name</label>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={filters.agencyName}
                    onChange={(e) => setFilters(prev => ({ ...prev, agencyName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                  <input
                    type="text"
                    placeholder="Search by customer..."
                    value={filters.customerName}
                    onChange={(e) => setFilters(prev => ({ ...prev, customerName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                  <select
                    value={filters.assigned_to}
                    onChange={(e) => setFilters(prev => ({ ...prev, assigned_to: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">All Users</option>
                    {teamMembers.map(member => (
                      <option key={member.id} value={member.id}>{member.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created From</label>
                  <input
                    type="date"
                    value={filters.created_from}
                    onChange={(e) => setFilters(prev => ({ ...prev, created_from: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created To</label>
                  <input
                    type="date"
                    value={filters.created_to}
                    onChange={(e) => setFilters(prev => ({ ...prev, created_to: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">All</option>
                    <option value="assigned">Assigned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="paused">Paused</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={filters.priority}
                    onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">All</option>
                    <option value="urgent">Urgent</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">Showing {filteredAssignments.length} results</div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => setShowTeamModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                  >
                    View Team Performance
                  </button>
                  <button 
                    onClick={() => setFilters({ status: '', priority: '', assigned_to: '', created_from: _today, created_to: _today, enquiryId: '', requestId: '', agencyName: '', customerName: '' })} 
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Team Performance - Removed, now in modal */}

        {/* Assignments Table */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Current Assignments</h2>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enquiry</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agency/Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Working Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Travel Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAssignments.map((assignment) => {
                  const isOverdue = isAssignmentOverdue(assignment);
                  return (
                  <tr key={assignment.id} className={`hover:bg-gray-50 ${isOverdue ? 'bg-red-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 font-mono">{(assignment as any).quotation_number ? `${(assignment as any).quotation_number}/${(assignment as any).version_number}` : `WN_${assignment.enquiry_id}`}</div>
                      <div className="text-sm text-gray-500 max-w-xs truncate">{assignment.plan_text}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{assignment.agency_name}</div>
                      <div className="text-sm text-gray-500">{assignment.customer_name || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{assignment.assigned_to_name || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(assignment.status)}`}>
                        {assignment.status === 'paused' ? (
                          <div className="flex flex-col">
                            <span>Paused</span>
                            {assignment.pause_reason && (
                              <span className="text-xs opacity-75 mt-0.5">{assignment.pause_reason}</span>
                            )}
                          </div>
                        ) : (
                          assignment.status.replace('_', ' ')
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(assignment.priority)}`}>
                        {assignment.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                        {calculateWorkingTime(assignment)}
                        {isOverdue && <span className="ml-1">⚠️</span>}
                      </div>
                      {isOverdue && (
                        <div className="text-xs text-red-500">Overdue ({'>'}30min active)</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(assignment.travel_date).toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleViewEnquiry(assignment)} className="text-blue-600 hover:text-blue-900" title="View">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleReassign(assignment)} className="text-green-600 hover:text-green-900" title="Reassign">
                          <UserPlus className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleEdit(assignment)} className="text-orange-600 hover:text-orange-900" title="Edit">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDelete(assignment.enquiry_id)} className="text-red-600 hover:text-red-900" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            
            {filteredAssignments.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                No assignments found
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enquiry Details Modal */}
      {showModal && selectedEnquiry && (
        <EnquiryDetailsModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          enquiry={selectedEnquiry}
        />
      )}

      {/* Reassign Modal */}
      {showReassignModal && selectedAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowReassignModal(false)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Reassign Enquiry</h2>
              <button onClick={() => setShowReassignModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Enquiry: <span className="font-medium font-mono">{(selectedAssignment as any).quotation_number ? `${(selectedAssignment as any).quotation_number}/${(selectedAssignment as any).version_number}` : `WN_${selectedAssignment.enquiry_id}`}</span></p>
              <p className="text-sm text-gray-600 mb-4">Currently assigned to: <span className="font-medium">{selectedAssignment.assigned_to_name}</span></p>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reassign to:</label>
              <select value={reassignUserId || ''} onChange={(e) => setReassignUserId(Number(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">Select team member</option>
                {teamMembers.map(member => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={confirmReassign} disabled={!reassignUserId} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed">
                Reassign
              </button>
              <button onClick={() => setShowReassignModal(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Performance Modal */}
      {showTeamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Team Performance</h2>
                <button onClick={() => setShowTeamModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="mb-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">From Date</label>
                  <input type="date" value={teamDateFrom} onChange={(e) => setTeamDateFrom(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">To Date</label>
                  <input type="date" value={teamDateTo} onChange={(e) => setTeamDateTo(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div className="mb-4 flex gap-2">
                <button onClick={() => fetchDashboardData(teamDateFrom, teamDateTo)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">Apply Filter</button>
                <button onClick={() => { setTeamDateFrom(''); setTeamDateTo(''); fetchDashboardData(); }} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">Clear</button>
              </div>
              <div className="mb-6 grid grid-cols-2 gap-4">
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <p className="text-sm font-medium text-blue-700">No. of Enquiries</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {teamMembers.reduce((sum, m) => sum + (m.enquiry_requests_completed || 0), 0)}
                  </p>
                </div>
                <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
                  <p className="text-sm font-medium text-purple-700">No. of Itineraries</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {teamMembers.reduce((sum, m) => sum + (m.total_itineraries_created || 0), 0)}
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team Member</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Requests Done</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Itineraries Done</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Avg/Request</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Avg/Itinerary</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pause Time</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Efficiency</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {teamMembers.map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div>
                            <div className="font-medium text-gray-900">{member.name}</div>
                            <div className="text-sm text-gray-500">{member.email}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">{member.enquiry_requests_completed}</span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">{member.total_itineraries_created}</span>
                        </td>
                        <td className="px-4 py-4 text-center text-sm text-gray-900">{formatHours(member.avg_time_per_request)}</td>
                        <td className="px-4 py-4 text-center text-sm text-gray-900">{formatHours(member.avg_time_per_itinerary)}</td>
                        <td className="px-4 py-4 text-center text-sm text-gray-900">{formatHours(member.total_pause_time)}</td>
                        <td className="px-4 py-4 text-center">
                          <div className="flex items-center justify-center">
                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                              <div className="bg-green-500 h-2 rounded-full" style={{ width: `${member.efficiency_score}%` }}></div>
                            </div>
                            <span className="text-xs text-gray-600">{member.efficiency_score.toFixed(0)}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {teamMembers.length === 0 && (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No team members found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkDashboard;